import React, { ChangeEvent } from 'react';
import { useFormik } from 'formik';

import UserProfileFormView from './UserProfileFormView';

import { useMytaverse } from '../../../providers/MytaverseProvider';

import { sendCustomSentryBreadcrumb } from '../../../helpers/sentry';
import { getCatchErrorMessage } from '../../../helpers/error';

import { UserChangeValidationSchema } from './validations';

import { InitialValues, IUserProfileForm } from './interfaces';

const UserProfileForm = ({
  updImage,
  onClose,
  openUpload,
  setOpenUpload,
  onUpdateMessage,
}: IUserProfileForm) => {
  const { user, changeUserProfile } = useMytaverse();

  const {
    profileImage,
    email,
    name,
    company = '',
    phoneNumber = '',
    linkedIn = '',
  } = user;

  const avatarSrc = updImage
    ? updImage instanceof File
      ? URL.createObjectURL(updImage)
      : updImage
    : profileImage.url;

  const onSubmit = async () => {
    try {
      onClose();
      await changeUserProfile(values);
      if (onUpdateMessage) {
        onUpdateMessage();
      }
      sendCustomSentryBreadcrumb({
        message: `update user profile = ${JSON.stringify(values)}`,
      });
    } catch (error: unknown) {
      throw Error(getCatchErrorMessage(error));
    }
  };

  const {
    values,
    errors,
    touched,
    setFieldTouched,
    handleChange,
    handleSubmit,
  } = useFormik<InitialValues>({
    initialValues: {
      username: name,
      company,
      phoneNumber,
      linkedIn,
      profileImage: updImage || user.profileImage,
    },
    onSubmit,
    validationSchema: UserChangeValidationSchema,
    enableReinitialize: true,
  });

  const handleInputChange = (
    evt: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setFieldTouched(evt.target.name, false, false);
    handleChange(evt);
  };

  const handleOpenUploadModal = () => setOpenUpload(!openUpload);

  return (
    <UserProfileFormView
      email={email}
      avatarSrc={avatarSrc as string}
      values={values}
      errors={errors}
      touched={touched}
      onChange={handleInputChange}
      onSubmit={handleSubmit}
      onCloseButton={onClose}
      onOpenUploadModal={handleOpenUploadModal}
    />
  );
};

export default UserProfileForm;
